import { useRouter } from 'next/router';
import { useMemo } from 'react';

import {
  Building2Icon,
  CloudIcon,
  CodeXmlIcon,
  Columns3Icon,
  CreditCardIcon,
  HistoryIcon,
  KeyRoundIcon,
  LinkIcon,
  RadarIcon,
  ScanFaceIcon,
  ScrollTextIcon,
  SquarePlusIcon,
  TagsIcon,
  UsersIcon,
} from '@air/design-icons';

import { type SettingsNavItem } from '~/components/v2/components/workspace/settings-nav/settingsNavTypes';
import { SETTINGS_TITLES } from '~/components/v2/constants/settingsTitles';
import { Routes } from '~/constants/routes';
import { useCanViewImportHistory } from '~/hooks/useCanViewImportHistory';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useIsCreativePerformanceEnabled } from '~/hooks/useIsCreativePerformanceEnabled';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCanViewPrivateAddOn } from '~/swr-hooks/workspaces/addOns/useCanViewPrivateAddOn';
import { insertIf } from '~/utils/insertIf';
import { isPathnameOrNested } from '~/utils/isPathnameOrNested';
import {
  canChangeWorkspaceDetails,
  canCreateTag,
  canDeleteCdnLink,
  canEditAddOns,
  canEditPerson,
  canManageSubscription,
  canManageWorkspaceSecurity,
  canSeeCustomFields,
  canSeeWorkspaceMembers,
  canViewAnalyticsConnections,
} from '~/utils/permissions/workspacePermissions';

export const useWorkspaceSettingsNavItems = () => {
  const { pathname } = useRouter();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const { canManagePeople, canSeeAddOns } = useFeatureFlags();
  const { canViewAddOn: canSeePrivatePeople } = useCanViewPrivateAddOn({ key: 'facialRecognition' });
  const { canViewAddOn: canViewCdnLinks } = useCanViewPrivateAddOn({
    key: 'cdnLinks',
  });
  const canEditAddonsCheck = canEditAddOns(permissions);

  const { isEnabled: isCreativePerformanceEnabled } = useIsCreativePerformanceEnabled();
  const canViewImportHistory = useCanViewImportHistory();

  const items = useMemo<SettingsNavItem[]>(
    () => [
      ...insertIf(canSeeWorkspaceMembers(permissions), () => [
        {
          id: 'users',
          name: SETTINGS_TITLES.USER_MANAGEMENT,
          isActive: isPathnameOrNested(pathname, Routes.workspaceSettings.members),
          href: Routes.workspaceSettings.members,
          icon: UsersIcon,
        },
      ]),
      ...insertIf(canChangeWorkspaceDetails(permissions), () => [
        {
          id: 'workspace',
          name: SETTINGS_TITLES.WORKSPACE_DETAILS,
          isActive: isPathnameOrNested(pathname, Routes.workspaceAdmin.edit),
          href: Routes.workspaceAdmin.edit,
          icon: Building2Icon,
        },
        {
          id: 'share-link-defaults',
          name: SETTINGS_TITLES.SHARE_LINK_DEFAULTS,
          isActive: isPathnameOrNested(pathname, Routes.workspaceAdmin.shareLinkDefaults),
          href: Routes.workspaceAdmin.shareLinkDefaults,
          icon: LinkIcon,
        },
      ]),
      ...insertIf(canSeeCustomFields(permissions), () => [
        {
          id: 'fields',
          name: SETTINGS_TITLES.CUSTOM_FIELDS,
          isActive: isPathnameOrNested(pathname, Routes.workspaceAdmin.customFields),
          href: Routes.workspaceAdmin.customFields,
          icon: Columns3Icon,
        },
      ]),
      ...insertIf(
        canManagePeople && canSeePrivatePeople && canEditPerson(permissions) && canChangeWorkspaceDetails(permissions),
        () => [
          {
            id: 'people',
            name: SETTINGS_TITLES.FACIAL_RECOGNITION,
            isActive: isPathnameOrNested(pathname, Routes.workspaceAdmin.people),
            href: Routes.workspaceAdmin.people,
            icon: ScanFaceIcon,
          },
        ],
      ),
      ...insertIf(canCreateTag(permissions), () => [
        {
          id: 'tags',
          name: SETTINGS_TITLES.YOUR_TAGS,
          isActive: isPathnameOrNested(pathname, Routes.workspaceAdmin.tags),
          href: Routes.workspaceAdmin.tags,
          icon: TagsIcon,
        },
      ]),
      ...insertIf(canDeleteCdnLink(permissions) && canViewCdnLinks, () => [
        {
          id: 'cdn-links',
          name: SETTINGS_TITLES.CDN_LINKS,
          isActive: isPathnameOrNested(pathname, Routes.workspaceAdmin.cdnLinks),
          href: Routes.workspaceAdmin.cdnLinks,
          icon: CodeXmlIcon,
        },
      ]),
      ...insertIf(canViewImportHistory, () => [
        {
          id: 'imports',
          name: SETTINGS_TITLES.IMPORT_HISTORY,
          isActive: isPathnameOrNested(pathname, Routes.workspaceAdmin.imports),
          href: Routes.workspaceAdmin.imports,
          icon: HistoryIcon,
        },
      ]),
      ...insertIf(canManageSubscription(permissions), () => [
        {
          id: 'plan',
          name: SETTINGS_TITLES.CURRENT_PLAN,
          isActive: isPathnameOrNested(pathname, Routes.workspaceAdmin.subscription),
          href: Routes.workspaceAdmin.subscription,
          icon: CloudIcon,
        },
      ]),
      ...insertIf(canManageSubscription(permissions), () => [
        {
          id: 'invoices',
          name: SETTINGS_TITLES.INVOICES_BILLING,
          isActive: isPathnameOrNested(pathname, Routes.workspaceAdmin.invoices),
          href: Routes.workspaceAdmin.invoices,
          icon: CreditCardIcon,
        },
      ]),
      ...insertIf(canChangeWorkspaceDetails(permissions) && canSeeAddOns && canEditAddonsCheck, () => [
        {
          id: 'add-ons',
          name: SETTINGS_TITLES.ADD_ONS,
          isActive: isPathnameOrNested(pathname, Routes.workspaceAdmin.addOns),
          href: Routes.workspaceAdmin.addOns,
          icon: SquarePlusIcon,
        },
      ]),
      ...insertIf(
        canChangeWorkspaceDetails(permissions) &&
          isCreativePerformanceEnabled &&
          canViewAnalyticsConnections(permissions),
        () => [
          {
            id: 'performance-analytics',
            name: SETTINGS_TITLES.ADS_ACCOUNTS,
            isActive: isPathnameOrNested(pathname, Routes.workspaceAdmin.adInsightsAccounts),
            href: Routes.workspaceAdmin.adInsightsAccounts,
            icon: RadarIcon,
          },
        ],
      ),
      ...insertIf(canChangeWorkspaceDetails(permissions), () => [
        {
          id: 'security-identity',
          name: SETTINGS_TITLES.SECURITY_IDENTITY,
          isActive: isPathnameOrNested(pathname, Routes.workspaceAdmin.security),
          href: Routes.workspaceAdmin.security,
          icon: KeyRoundIcon,
        },
      ]),
      ...insertIf(canManageWorkspaceSecurity(permissions), () => [
        {
          id: 'audit-logs',
          name: SETTINGS_TITLES.AUDIT_LOGS,
          isActive: isPathnameOrNested(pathname, Routes.workspaceAdmin.auditLogs),
          href: Routes.workspaceAdmin.auditLogs,
          icon: ScrollTextIcon,
        },
      ]),
    ],
    [
      pathname,
      permissions,
      canManagePeople,
      canSeePrivatePeople,
      canViewCdnLinks,
      canEditAddonsCheck,
      canSeeAddOns,
      canViewImportHistory,
      isCreativePerformanceEnabled,
    ],
  );

  return { items };
};
